"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, Activity, Zap, Heart, Briefcase } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function AppointmentForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="appointment-form" ref={ref} className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className={`text-center mb-12 transition-all duration-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
          <h2 className="text-[36px] md:text-[48px] font-display font-bold text-secondary mb-4">
            Appointment Form
          </h2>
          <p className="text-muted text-lg max-w-2xl mx-auto">
            Schedule your personalized physiotherapy session with our expert team. We're here to help you recover and perform at your best.
          </p>
        </div>

        <div className={`grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 transition-all duration-300 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
          <ServiceIcon icon={Activity} label="Sports Physiotherapy" delay={0} isVisible={isVisible} />
          <ServiceIcon icon={Zap} label="Back & Neck Pain" delay={50} isVisible={isVisible} />
          <ServiceIcon icon={Heart} label="Post-operative Rehabilitation" delay={100} isVisible={isVisible} />
          <ServiceIcon icon={Briefcase} label="Workplace & Posture Injuries" delay={150} isVisible={isVisible} />
        </div>

        <FormProvider className="bg-white rounded-md shadow-soft border border-border p-8" formId="appointment_form_book_appointment" sectionName="appointment-form" defaultValues={{
        full_name: "",
        email: "",
        phone: "",
        service: "",
        appointment_date: "",
        appointment_time: "",
        notes: "",
        consent: false
      }} validationRules={{
        full_name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        service: {
          required: true
        },
        appointment_date: {
          required: true
        },
        appointment_time: {
          required: true
        },
        consent: {
          required: true
        }
      }} submitText="Confirm Booking" successMessage="Your appointment request has been received! We'll confirm your booking shortly via email or SMS.">
          <AppointmentFormContent isVisible={isVisible} />
        </FormProvider>
      </div>
    </section>;
}
function ServiceIcon({
  icon: Icon,
  label,
  delay,
  isVisible
}: {
  icon: any;
  label: string;
  delay: number;
  isVisible: boolean;
}) {
  return <div className={`flex flex-col items-center text-center transition-all duration-300`} style={{
    transitionDelay: `${delay}ms`,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(20px)"
  }}>
      <div className="w-16 h-16 rounded-md border border-border flex items-center justify-center mb-3 hover:border-primary transition-colors duration-150">
        <Icon className="size-8 text-secondary" strokeWidth={1.5} />
      </div>
      <p className="text-sm text-muted-foreground leading-tight">{label}</p>
    </div>;
}
function AppointmentFormContent({
  isVisible
}: {
  isVisible: boolean;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light border border-primary/20 rounded-md text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
          <Heart className="size-8 text-primary" />
        </div>
        <h3 className="text-[28px] font-display font-bold text-secondary mb-3">
          Booking Received!
        </h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="btn-primary">
          Book Another Appointment
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem className="transition-all duration-300" style={{
        transitionDelay: "0ms",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)"
      }}>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="border-muted focus:border-primary" />
              </FormControl>
              <FormDescription>Enter your first and last name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required"
      }} render={({
        field
      }) => <FormItem className="transition-all duration-300" style={{
        transitionDelay: "50ms",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)"
      }}>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="border-muted focus:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem className="transition-all duration-300" style={{
        transitionDelay: "100ms",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)"
      }}>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+61 4XX XXX XXX" {...field} className="border-muted focus:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="service" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem className="transition-all duration-300" style={{
        transitionDelay: "150ms",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)"
      }}>
              <FormLabel>Select Service</FormLabel>
              <FormControl>
                <select {...field} className="border-input placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border-muted focus:border-primary">
                  <option value="">Choose a service</option>
                  <option value="Sports Physiotherapy">Sports Physiotherapy</option>
                  <option value="Back & Neck Pain">Back & Neck Pain</option>
                  <option value="Post-operative Rehabilitation">Post-operative Rehabilitation</option>
                  <option value="Workplace & Posture Injuries">Workplace & Posture Injuries</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="appointment_date" rules={{
        required: "Please select a date"
      }} render={({
        field
      }) => <FormItem className="transition-all duration-300" style={{
        transitionDelay: "200ms",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)"
      }}>
              <FormLabel>Preferred Appointment Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} className="border-muted focus:border-primary" />
              </FormControl>
              <FormDescription>Select a date that works for you.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="appointment_time" rules={{
        required: "Please select a time slot"
      }} render={({
        field
      }) => <FormItem className="transition-all duration-300" style={{
        transitionDelay: "250ms",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)"
      }}>
              <FormLabel>Preferred Time Slot</FormLabel>
              <FormControl>
                <select {...field} className="border-input placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border-muted focus:border-primary">
                  <option value="">Select a time</option>
                  <option value="09:00 AM - 09:30 AM">09:00 AM - 09:30 AM</option>
                  <option value="09:30 AM - 10:00 AM">09:30 AM - 10:00 AM</option>
                  <option value="10:00 AM - 10:30 AM">10:00 AM - 10:30 AM</option>
                  <option value="10:30 AM - 11:00 AM">10:30 AM - 11:00 AM</option>
                  <option value="11:00 AM - 11:30 AM">11:00 AM - 11:30 AM</option>
                  <option value="11:30 AM - 12:00 PM">11:30 AM - 12:00 PM</option>
                  <option value="12:00 PM - 12:30 PM">12:00 PM - 12:30 PM</option>
                  <option value="12:30 PM - 01:00 PM">12:30 PM - 01:00 PM</option>
                  <option value="01:00 PM - 01:30 PM">01:00 PM - 01:30 PM</option>
                  <option value="01:30 PM - 02:00 PM">01:30 PM - 02:00 PM</option>
                  <option value="02:00 PM - 02:30 PM">02:00 PM - 02:30 PM</option>
                  <option value="02:30 PM - 03:00 PM">02:30 PM - 03:00 PM</option>
                  <option value="03:00 PM - 03:30 PM">03:00 PM - 03:30 PM</option>
                  <option value="03:30 PM - 04:00 PM">03:30 PM - 04:00 PM</option>
                  <option value="04:00 PM - 04:30 PM">04:00 PM - 04:30 PM</option>
                  <option value="04:30 PM - 05:00 PM">04:30 PM - 05:00 PM</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="notes" render={({
      field
    }) => <FormItem className="mt-6 transition-all duration-300" style={{
      transitionDelay: "300ms",
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(20px)"
    }}>
            <FormLabel>Additional Information (optional)</FormLabel>
            <FormControl>
              <Textarea placeholder="Any injuries, concerns, or preferences..." rows={4} {...field} className="border-muted focus:border-primary resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <input type="hidden" name="email_to" value="bookings@axionphysiotherapy.com.au" />

      <FormField control={control} name="consent" rules={{
      required: "You must agree to the privacy policy"
    }} render={({
      field
    }) => <FormItem className="mt-6 flex items-start space-x-3 transition-all duration-300" style={{
      transitionDelay: "350ms",
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(20px)"
    }}>
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="mt-1 size-4 rounded border-muted text-primary focus:ring-2 focus:ring-primary focus:ring-offset-0" />
            </FormControl>
            <div className="flex-1">
              <FormLabel className="font-normal text-sm leading-relaxed">
                I agree to the privacy policy and to receive appointment confirmation via email/SMS.
              </FormLabel>
              <FormDescription>
                Your data will be used only for booking and communication purposes.
              </FormDescription>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md transition-all duration-300" style={{
      transitionDelay: "400ms",
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(20px)"
    }}>
          <p className="text-red-800 text-sm">{submitError}</p>
        </div>}

      <div className="mt-8 flex justify-center transition-all duration-300" style={{
      transitionDelay: "400ms",
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(20px)"
    }}>
        <Button type="submit" disabled={isSubmitting} className="btn-primary px-12 py-3 text-base">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing...
            </> : "Book Now"}
        </Button>
      </div>
    </Form>;
}